import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router";
import { Icon } from "../components/icons";
import { Avatar, Badge, Btn, Confirm, Empty, ErrorNote, Field, Loading, Modal, Notice, PageHead, Section, ShownOnce } from "../components/kit";
import { api, identity, type Account, type AccountDetail, type AuditEntry, type Client, type Role } from "../lib/api";
import { ago, formatDate, formatDateTime } from "../lib/format";
import { useAction, useResource } from "../lib/hooks";
import { AccountBadges } from "./Accounts";
import { AuditList } from "./Audit";

const MOBILE = /iPhone|iPad|Android|Mobile/i;

export function AccountDetailPage({ me }: { me: Account }) {
  const { id = "" } = useParams();
  const { data, error, reload } = useResource<AccountDetail>(`/accounts/${id}`);
  const audit = useResource<{ entries: AuditEntry[] }>(`/audit?target_kind=account&target_id=${id}&limit=10`);
  const portals = useResource<{ clients: Client[] }>("/clients");
  const action = useAction();
  const [password, setPassword] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  if (error?.status === 404) {
    return (
      <>
        <PageHead
          eyebrow="Accounts"
          title="No such account."
          back={
            <Link to="/accounts" className="link back">
              ← All accounts
            </Link>
          }
        />
      </>
    );
  }
  if (!data) return error ? <ErrorNote error={error} /> : <Loading />;

  const a = data.account;
  const self = a.id === me.id;
  const refresh = () => {
    reload();
    audit.reload();
  };
  const post = (path: string) => action.run(() => api("POST", `/accounts/${a.id}/${path}`)).then(refresh);
  const portalName = (cid: string) => portals.data?.clients.find((c) => c.id === cid)?.name ?? cid;
  const alarming = data.events.filter((e) => e.alarming).length;
  const methods = [a.email && "Email and password", a.phone && "SMS code"].filter(Boolean).join(" · ");

  return (
    <>
      <div className="page-over">
        <Link to="/accounts" className="link back">
          ← Accounts
        </Link>
        <span className="page-eyebrow">{self ? "Your account" : "Account"}</span>
      </div>

      <section className="profile" data-disabled={a.disabled || undefined}>
        <div className="profile-main">
          <span className="profile-avatar">
            <Avatar name={a.name} large />
            {!a.disabled && data.sessions.length > 0 && <span className="profile-online" title="Signed in now" />}
          </span>
          <div className="profile-who">
            <h1>
              {a.name}
              {self && <span className="profile-you">You</span>}
            </h1>
            <p className="profile-ids">
              {a.email && (
                <span>
                  <Icon name="mail" size={14} />
                  {a.email}
                </span>
              )}
              {a.phone && (
                <span>
                  <Icon name="phone" size={14} />
                  {a.phone}
                </span>
              )}
            </p>
            <AccountBadges account={a} />
          </div>
          <div className="profile-actions">
            <Btn icon="edit" onClick={() => setEditing(true)}>
              Edit
            </Btn>
          </div>
        </div>
        <dl className="profile-stats">
          <div>
            <dt>Role</dt>
            <dd>{a.role === "admin" ? "Administrator" : "Member"}</dd>
          </div>
          <div>
            <dt>Last sign-in</dt>
            <dd title={a.last_sign_in_at ? formatDateTime(a.last_sign_in_at) : undefined}>{a.last_sign_in_at ? ago(a.last_sign_in_at) : "Never"}</dd>
          </div>
          <div>
            <dt>Signed in on</dt>
            <dd>
              {data.sessions.length} {data.sessions.length === 1 ? "browser" : "browsers"}
            </dd>
          </div>
          <div>
            <dt>Member since</dt>
            <dd>{formatDate(a.created_at)}</dd>
          </div>
        </dl>
      </section>

      {a.disabled && (
        <Notice tone="danger" title="This account is disabled.">
          It cannot sign in anywhere{a.disabled_at ? ` (since ${formatDateTime(a.disabled_at)})` : ""}. Enabling it lets it sign in again with its current password.
        </Notice>
      )}
      {self && <Notice title="This is you.">Change your own password and two-step sign-in at id.mashriqain.com. You cannot disable or demote yourself here.</Notice>}
      <ErrorNote error={action.error} />

      <div className="split">
        <div className="stack">
          <Section
            title="Signed in on"
            aside={
              <Confirm
                small
                label="Sign out everywhere"
                title="Sign out of every browser?"
                body={<p>Every session of this account ends, and every portal it signed in to loses its refresh tokens.</p>}
                confirm="Sign out everywhere"
                disabled={data.sessions.length === 0}
                onConfirm={() => post("end-sessions")}
              />
            }
          >
            {data.sessions.length === 0 ? (
              <Empty title="Not signed in anywhere." />
            ) : (
              <ul role="list" className="rows">
                {data.sessions.map((s) => (
                  <li key={s.id}>
                    <span className="row-icon">
                      <Icon name={MOBILE.test(s.device) ? "phone" : "device"} size={18} />
                    </span>
                    <div className="row-grow">
                      <span className="row-title">{s.device}</span>
                      <span className="row-sub">
                        {s.site || "Mashriqain"} · since {ago(s.created_at)}
                        {s.ip && <span className="code"> · {s.ip}</span>}
                      </span>
                    </div>
                    <Btn small onClick={() => action.run(() => api("DELETE", `/accounts/${a.id}/sessions/${s.id}`)).then(refresh)}>
                      Sign out
                    </Btn>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section
            title="Activity"
            aside={alarming > 0 ? <Badge tone="warn">{alarming} to check</Badge> : <span className="muted small">What they see on their account page</span>}
          >
            {data.events.length === 0 ? (
              <Empty title="No activity yet." />
            ) : (
              <ol role="list" className="timeline">
                {data.events.map((e, i) => (
                  <li key={i} data-alarming={e.alarming || undefined}>
                    <div className="timeline-what">
                      <strong>{e.description}</strong>
                      {e.detail && <span className="muted"> · {e.detail}</span>}
                      {e.alarming && (
                        <>
                          {" "}
                          <Badge tone="warn">Check</Badge>
                        </>
                      )}
                    </div>
                    <div className="timeline-meta">
                      <time dateTime={e.created_at} title={formatDateTime(e.created_at)}>
                        {ago(e.created_at)}
                      </time>
                      {e.device && <span>{e.device}</span>}
                      {e.ip && <span className="code">{e.ip}</span>}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </Section>

          <Section title="Changes by administrators" aside={<span className="muted small">From the audit log</span>}>
            {audit.data ? <AuditList entries={audit.data.entries} /> : <Loading />}
          </Section>
        </div>

        <div className="stack">
          <Section title="Security">
            <ul role="list" className="rows security-rows">
              <li>
                <span className="row-icon">
                  <Icon name="key" size={18} />
                </span>
                <div className="row-grow">
                  <span className="row-title">Password</span>
                  <span className="row-sub">{a.email ? `Changed ${ago(a.password_changed_at)}` : "None · signs in by SMS"}</span>
                </div>
                <Confirm
                  small
                  label="Reset"
                  disabled={self || !a.email}
                  title="Reset the password?"
                  body={<p>A new password is generated and shown once. {a.name} is signed out of every browser and portal, and has to sign in with the new password.</p>}
                  confirm="Reset password"
                  onConfirm={async () => {
                    const out = await action.run(() => api<{ password: string }>("POST", `/accounts/${a.id}/password`));
                    if (out) setPassword(out.password);
                    refresh();
                  }}
                />
              </li>
              <li>
                <span className="row-icon">
                  <Icon name="shield" size={18} />
                </span>
                <div className="row-grow">
                  <span className="row-title">
                    Two-step sign-in {a.two_step ? <Badge tone="ok">On</Badge> : <Badge>Off</Badge>}
                  </span>
                  <span className="row-sub">
                    {a.two_step ? `${data.recovery_codes_left} recovery codes left` : "Only they can turn it on, at id.mashriqain.com"}
                  </span>
                </div>
                <Confirm
                  small
                  label="Turn off"
                  title="Turn off two-step sign-in?"
                  body={<p>Use this when {a.name} has lost both the authenticator and the recovery codes. They can sign in with the password alone until they turn it on again.</p>}
                  confirm="Turn it off"
                  disabled={self || !a.two_step}
                  onConfirm={() => post("reset-two-step")}
                />
              </li>
              <li>
                <span className="row-icon">
                  <Icon name="phone" size={18} />
                </span>
                <div className="row-grow">
                  <span className="row-title">Ways to sign in</span>
                  <span className="row-sub">{methods || "None"}</span>
                </div>
              </li>
            </ul>
          </Section>

          <Section title="Portals" aside={<span className="muted small">{data.clients.length} listed</span>}>
            {data.clients.length === 0 ? (
              <p className="muted small">Not listed on any portal with listed-only access. Portals open to everyone still let this account in.</p>
            ) : (
              <ul role="list" className="rows">
                {data.clients.map((c) => (
                  <li key={c}>
                    <Link to={`/portals/${c}`} className="rows-link">
                      <span className="row-icon">
                        <Icon name="portals" size={18} />
                      </span>
                      <span className="row-grow">
                        <span className="row-title">{portalName(c)}</span>
                        <span className="row-sub code">{c}</span>
                      </span>
                      <Icon name="chevron" size={16} />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title={a.disabled ? "Account disabled" : "Danger zone"} className="danger-zone">
            <p className="muted small">
              {a.disabled
                ? `${a.name} cannot sign in anywhere. Enabling the account lets them sign in again with their current password.`
                : `Disabling signs ${a.name} out everywhere at once and stops every sign-in until an administrator enables it again. Nothing is deleted.`}
            </p>
            <div>
              {a.disabled ? (
                <Confirm label="Enable account" title="Enable this account?" body={<p>{a.name} will be able to sign in again.</p>} confirm="Enable" variant="primary" onConfirm={() => post("enable")} />
              ) : (
                <Confirm
                  label="Disable account"
                  title="Disable this account?"
                  body={<p>{a.name} is signed out everywhere at once and cannot sign in to anything until an administrator enables the account again. Nothing is deleted.</p>}
                  confirm="Disable account"
                  disabled={self}
                  onConfirm={() => post("disable")}
                />
              )}
            </div>
          </Section>
        </div>
      </div>

      <Modal open={password !== null} title="New password" onClose={() => setPassword(null)}>
        {password && <ShownOnce label={`Password for ${identity(a)}`} value={password} />}
        <div className="modal-actions">
          <Btn variant="primary" onClick={() => setPassword(null)}>
            Done
          </Btn>
        </div>
      </Modal>
      <EditAccount key={`${a.name}:${a.role}:${a.phone}`} open={editing} account={a} self={self} onClose={() => setEditing(false)} onSaved={refresh} />
    </>
  );
}

function EditAccount({ open, account, self, onClose, onSaved }: { open: boolean; account: Account; self: boolean; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(account.name);
  const [phone, setPhone] = useState(account.phone);
  const [role, setRole] = useState<Role>(account.role);
  const { busy, error, run, clear } = useAction();
  const close = () => {
    clear();
    onClose();
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const out = await run(() => api("PATCH", `/accounts/${account.id}`, { name, role, phone }));
    if (out) {
      onSaved();
      close();
    }
  };
  return (
    <Modal open={open} title="Edit account" onClose={close}>
      <form className="form" onSubmit={submit}>
        <Field label="Name" error={error?.fields.name}>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} />
        </Field>
        <Field label="Email" hint="The email is the sign-in name and cannot be changed here.">
          <input className="input" value={account.email || "None"} disabled />
        </Field>
        <Field label="Mobile number" hint="Lets the account sign in with a code by SMS. Leave empty to remove it." error={error?.fields.phone}>
          <input className="input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="01712345678" required={!account.email} />
        </Field>
        <Field label="Role" hint={self ? "You cannot remove your own administrator role." : "Administrators can open this panel."}>
          <select className="input" value={role} onChange={(e) => setRole(e.target.value as Role)} disabled={self}>
            <option value="member">Member</option>
            <option value="admin">Administrator</option>
          </select>
        </Field>
        {error && !error.fields.name && !error.fields.phone && <ErrorNote error={error} />}
        <div className="modal-actions">
          <Btn onClick={close}>Cancel</Btn>
          <Btn variant="primary" type="submit" disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
